import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Email as EmailIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import FriendsManager from '../utils/FriendsManager';
import { fetchAuthSession } from 'aws-amplify/auth';

interface SendFriendInviteDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const SendFriendInviteDialog: React.FC<SendFriendInviteDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [friendAdded, setFriendAdded] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setEmailSent(false);
    setFriendAdded(false);
  };

  const handleSendEmailInvite = async () => {
    if (!email) return;

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await FriendsManager.sendFriendInvite(
        {
          toEmail: email,
          inviteType: 'email',
        },
        token
      );

      if (result.success) {
        setEmailSent(true);
        setEmail('');
        setTimeout(() => {
          onClose();
          setEmailSent(false);
          if (onSuccess) onSuccess();
        }, 2000);
      }
    } catch (err) {
      console.error('Error sending email invite:', err);
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    console.log('handleGenerateLink called');

    console.log('Setting loading to true');
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching auth session');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      console.log('Auth token obtained:', !!token);

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Calling sendFriendInvite');
      const result = await FriendsManager.sendFriendInvite(
        {
          inviteType: 'link',
        },
        token
      );
      console.log('sendFriendInvite result:', result);

      if (result.success && result.inviteLink) {
        console.log('Setting invite link:', result.inviteLink);
        setInviteLink(result.inviteLink);
      } else {
        console.log('Result did not contain inviteLink:', result);
        setError('Failed to generate invite link - no link returned from server');
      }
    } catch (err) {
      console.error('Error generating invite link:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate invite link');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
    }
  };

  const handleShareLink = async () => {
    if (!inviteLink) return;

    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title: 'Join me on Famous Initials!',
          text: 'Play Famous Initials with me and compete on the leaderboard!',
          url: inviteLink,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback to copy
      handleCopyLink();
    }
  };

  const handleAddFriendByCode = async () => {
    if (!inviteCode) return;

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await FriendsManager.sendFriendRequestByCode(inviteCode.toUpperCase(), token);

      if (result.success) {
        setFriendAdded(true);
        setInviteCode('');
        setTimeout(() => {
          onClose();
          setFriendAdded(false);
          if (onSuccess) onSuccess();
        }, 2000);
      }
    } catch (err) {
      console.error('Error adding friend by code:', err);
      setError(err instanceof Error ? err.message : 'Failed to add friend');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setInviteCode('');
    setInviteLink(null);
    setError(null);
    setEmailSent(false);
    setFriendAdded(false);
    setCopySuccess(false);
    setTabValue(0);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Friend to Famous Initials</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<KeyIcon />} label="Invite Code" />
            <Tab icon={<EmailIcon />} label="Email Invite" />
            <Tab icon={<LinkIcon />} label="Share Link" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {emailSent && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Invitation sent successfully!
            </Alert>
          )}

          {friendAdded && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Friend added successfully!
            </Alert>
          )}

          {/* Invite Code Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your friend's 7-character invite code to add them instantly. They can find their code on their profile or friends page.
            </Typography>
            <TextField
              fullWidth
              label="Friend's Invite Code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="ABC1234"
              disabled={loading || friendAdded}
              inputProps={{ maxLength: 7, style: { textTransform: 'uppercase' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !loading && !friendAdded && inviteCode.length === 7) {
                  handleAddFriendByCode();
                }
              }}
            />
          </TabPanel>

          {/* Email Invite Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Send a friend invitation via email. They'll receive an email with a link to accept your friend request.
            </Typography>
            <TextField
              fullWidth
              label="Friend's Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@example.com"
              disabled={loading || emailSent}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !loading && !emailSent) {
                  handleSendEmailInvite();
                }
              }}
            />
          </TabPanel>

          {/* Share Link Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate a shareable link that you can send to your friends via any messaging app or social media.
            </Typography>

            {!inviteLink && (
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerateLink}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
              >
                {loading ? 'Generating...' : 'Generate Invite Link'}
              </Button>
            )}

            {inviteLink && (
              <Box>
                <TextField
                  fullWidth
                  label="Your Invite Link"
                  value={inviteLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopyLink} edge="end">
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyLink}
                  >
                    Copy Link
                  </Button>
                  {typeof navigator !== 'undefined' && 'share' in navigator && (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ShareIcon />}
                      onClick={handleShareLink}
                    >
                      Share
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  This link will expire in 30 days
                </Typography>
              </Box>
            )}
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {((tabValue === 0 && friendAdded) || (tabValue === 1 && emailSent)) ? 'Done' : 'Cancel'}
          </Button>
          {tabValue === 0 && !friendAdded && (
            <Button
              onClick={handleAddFriendByCode}
              variant="contained"
              disabled={inviteCode.length !== 7 || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Add Friend'}
            </Button>
          )}
          {tabValue === 1 && !emailSent && (
            <Button
              onClick={handleSendEmailInvite}
              variant="contained"
              disabled={!email || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Invitation'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Link copied to clipboard!"
      />
    </>
  );
};

export default SendFriendInviteDialog;
